import { useCallback, useRef, useState } from "react";
import api from "@/lib/api";

export const CHUNK = 8 * 1024 * 1024;
/** Limites par type (alignées sur le backend MEDIA_LIMITS). */
export const LIMITS = { photo: 50 * 1024 ** 2, plan: 500 * 1024 ** 2, video: 2 * 1024 ** 3 };
export const LIMIT_LABEL = { photo: "50 Mo", plan: "500 Mo", video: "2 Go" };
const KEY = (scope, f) => `crm.chunk.${scope}|${f.name}|${f.size}|${f.lastModified}`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function sha256(file) {
  if (file.size > 64 * 1024 * 1024 || !window.crypto?.subtle) return null;
  const buf = await file.arrayBuffer();
  const h = await window.crypto.subtle.digest("SHA-256", buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Envoi chunké (8 Mo) reprenable d'un fichier. `init(payload)` → POST .../chunk-upload/init ; `onProgress({status, progress, resumed, total})`. */
export async function uploadChunked({ file, kind, scope, init, onProgress = () => {}, isCancelled = () => false }) {
  const storeKey = KEY(scope, file);
  let uploadId = localStorage.getItem(storeKey);
  let missing = null, total = Math.ceil(file.size / CHUNK);
  if (uploadId) {
    try { const { data } = await api.get(`/crm/media/chunk-upload/${uploadId}/status`); if (data.status === "done" && data.media) { localStorage.removeItem(storeKey); return data.media; } if (data.status !== "uploading") throw new Error("expired"); missing = data.missing; total = data.total_chunks; onProgress({ resumed: data.received.length, total, status: "uploading" }); } catch { uploadId = null; localStorage.removeItem(storeKey); }
  }
  if (!uploadId) {
    onProgress({ status: "hashing" });
    const digest = await sha256(file);
    const { data } = await init({ kind, content_type: file.type, size: file.size, filename: file.name, sha256: digest });
    uploadId = data.upload_id; total = data.total_chunks; missing = [...Array(total).keys()];
    localStorage.setItem(storeKey, uploadId);
  }
  onProgress({ uploadId, status: "uploading", total });
  let sent = total - missing.length;
  for (const idx of missing) {
    if (isCancelled()) throw new Error("cancelled");
    const blob = file.slice(idx * CHUNK, Math.min(file.size, (idx + 1) * CHUNK));
    const fd = new FormData(); fd.append("file", blob, `chunk_${idx}`);
    let ok = false;
    for (let attempt = 0; attempt < 4 && !ok; attempt++) {
      try { await api.put(`/crm/media/chunk-upload/${uploadId}/chunk/${idx}`, fd, { timeout: 120000 }); ok = true; } catch (e) { if (attempt === 3) throw e; onProgress({ status: "retrying" }); await sleep(800 * (attempt + 1)); }
    }
    sent += 1;
    onProgress({ status: "uploading", progress: Math.round((sent / total) * 100), sentChunks: sent, loaded: Math.min(file.size, sent * CHUNK) });
  }
  onProgress({ status: "finalizing", progress: 100 });
  const { data } = await api.post(`/crm/media/chunk-upload/${uploadId}/complete`, {}, { timeout: 600000 });
  localStorage.removeItem(storeKey);
  return data;
}

/** Plan(s) de lot(s) en chunké, avec état compatible <UploadProgress/> ({ index, total, phase, name, loaded, size }). */
export async function uploadPlansChunked(files, initPayload, onProgress) {
  const list = [...files];
  for (let i = 0; i < list.length; i++) {
    const f = list[i];
    const base = { index: i, total: list.length, name: f.name, size: f.size, before: 0, after: 0 };
    onProgress?.({ ...base, phase: "upload", loaded: 0 });
    await uploadChunked({ file: f, kind: "plan", scope: initPayload.owner_id || initPayload.targets || "apply", init: (p) => api.post("/crm/files/chunk-upload/init", { ...p, ...initPayload }),
      onProgress: (p) => { if (p.loaded != null) onProgress?.({ ...base, phase: "upload", loaded: p.loaded }); } });
  }
  onProgress?.({ index: list.length, total: list.length, phase: "done", loaded: 1, size: 1, before: 0, after: 0 });
}

/** File d'attente d'uploads chunkés (médias projet) : état par fichier { id, name, size, kind, status, progress, error, uploadId }. */
export function useChunkUploader(projectId, { onDone } = {}) {
  const [items, setItems] = useState([]);
  const cancelled = useRef(new Set());
  const running = useRef(false);
  const queue = useRef([]);
  const patch = (id, p) => setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...(typeof p === "function" ? p(it) : p) } : it)));

  const uploadOne = (it) => uploadChunked({
    file: it.file, kind: it.kind, scope: projectId,
    init: (p) => api.post(`/crm/projects/${projectId}/media/chunk-upload/init`, p),
    onProgress: ({ loaded, ...p }) => patch(it.id, p), isCancelled: () => cancelled.current.has(it.id),
  });

  const pump = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    while (queue.current.length) {
      const it = queue.current.shift();
      if (cancelled.current.has(it.id)) continue;
      try { const media = await uploadOne(it); patch(it.id, { status: "done", progress: 100, media }); onDone?.(media); }
      catch (e) { patch(it.id, { status: e?.message === "cancelled" ? "cancelled" : "error", error: e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || "Échec" }); }
    }
    running.current = false;
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const enqueue = useCallback((files, kindOf) => {
    const add = files.map((file) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, name: file.name, size: file.size, kind: kindOf(file), status: "queued", progress: 0 })).filter((x) => x.kind);
    setItems((arr) => [...arr, ...add]);
    queue.current.push(...add);
    pump();
  }, [pump]);
  const retry = useCallback((id) => { setItems((arr) => { const it = arr.find((x) => x.id === id); if (it) { cancelled.current.delete(id); queue.current.push({ ...it, status: "queued" }); } return arr.map((x) => (x.id === id ? { ...x, status: "queued", error: null } : x)); }); setTimeout(pump, 0); }, [pump]);
  const cancel = useCallback((id) => { cancelled.current.add(id); patch(id, { status: "cancelled" }); }, []);
  const clearDone = useCallback(() => setItems((arr) => arr.filter((x) => !["done", "cancelled"].includes(x.status))), []);
  return { items, enqueue, retry, cancel, clearDone };
}

export const kindOfFile = (file) => (file.type.startsWith("video/") ? "video" : file.type === "application/pdf" ? "plan" : file.type.startsWith("image/") ? "photo" : null);
export const fmtSize = (n) => (n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} Go` : n >= 1024 ** 2 ? `${(n / 1024 ** 2).toFixed(1)} Mo` : `${Math.round(n / 1024)} Ko`);
